<template>
  <!-- ปุ่มลอย (FAB) — ลากย้ายตำแหน่งได้ (เจ้าของแจ้งว่าบังปุ่มอื่นบางจอ) ตำแหน่งที่ลากจำไว้ใน
       localStorage · ไม่ลาก (แตะเฉย ๆ) = เปิด/ปิด panel ตามเดิม แยกด้วยระยะขยับ > 4px -->
  <button ref="fab" class="cbfab" :class="{ open, dragging }" :style="fabStyle"
    @click="toggle" @pointerdown="onPointerDown" @pointermove="onPointerMove"
    @pointerup="onPointerUp" @pointercancel="onPointerUp"
    v-hint="$t('nav.chatbot')" :aria-label="$t('nav.chatbot')">
    <span class="mdi" :class="open ? 'mdi-close' : 'mdi-robot-happy-outline'"></span>
  </button>

  <!-- panel — Teleport to body: .topbar มี backdrop-filter ที่สร้าง containing block ใหม่ให้ position:fixed
       (ดู MENU_SCROPE §4) ถ้าไม่ teleport panel จะยึดกรอบแถบบนแทน viewport แล้วลอยผิดที่
       ปุ่มขยาย = ขยายขนาด panel เอง ไม่สลับ state/เมนู — งานที่ค้างอยู่หน้าอื่นจึงไม่หายไป -->
  <Teleport to="body">
    <div v-if="open" ref="panel" class="cbfab-panel" :class="{ expanded }" :style="panelPos || {}"
      role="dialog" aria-label="AI Chatbot">
      <header class="cbp-head" @pointerdown="onHeadDown" @pointermove="onHeadMove"
        @pointerup="onHeadUp" @pointercancel="onHeadUp">
        <button v-if="!expanded" class="cbp-ic" v-hint="'ประวัติการสนทนา'" aria-label="ประวัติการสนทนา"
          @click="toggleList"><span class="mdi mdi-forum-outline"></span></button>
        <span class="cbp-title"><span class="mdi mdi-robot-happy-outline"></span> {{ $t('nav.chatbot') }}</span>
        <span class="cbp-actions">
          <button class="cbp-ic" v-hint="'เริ่มบทสนทนาใหม่'" aria-label="เริ่มบทสนทนาใหม่" @click="newChat"><span class="mdi mdi-plus"></span></button>
          <button class="cbp-ic" v-hint="expanded ? 'ย่อ' : 'ขยาย'" :aria-label="expanded ? 'ย่อ' : 'ขยาย'" @click="expanded = !expanded">
            <span class="mdi" :class="expanded ? 'mdi-arrow-collapse' : 'mdi-arrow-expand'"></span>
          </button>
          <button class="cbp-ic" v-hint="'ปิด'" aria-label="ปิด" @click="close"><span class="mdi mdi-close"></span></button>
        </span>
      </header>

      <div class="cbp-body" :class="{ split: expanded }">
        <!-- รายการบทสนทนา — แสดงคู่กับแชทตอนขยาย, หรือสลับมาดูเดี่ยว ๆ ตอนย่อ (กดไอคอนประวัติ) -->
        <aside v-if="expanded || showList" class="cbp-list">
          <button class="cbp-new" @click="newChat"><span class="mdi mdi-plus"></span> เริ่มบทสนทนาใหม่</button>
          <div class="cbp-convs">
            <div v-for="c in conversations" :key="c.conversationid" class="cbp-conv-row"
              :class="{ on: c.conversationid === conversationId }">
              <button class="cbp-conv" @click="openConversation(c.conversationid)">
                <span class="cbp-conv-title">{{ c.title || 'บทสนทนา' }}</span>
              </button>
              <button v-if="pendingDeleteId !== c.conversationid" class="cbp-conv-del"
                v-hint="'ลบ'" aria-label="ลบบทสนทนานี้" @click.stop="pendingDeleteId = c.conversationid">
                <span class="mdi mdi-trash-can-outline"></span>
              </button>
              <span v-else class="cbp-conv-confirm">
                <button class="cbp-conv-yes" v-hint="'ยืนยันลบ'" aria-label="ยืนยันลบ" :disabled="deletingId === c.conversationid" @click.stop="doDelete(c.conversationid)"><span class="mdi mdi-check"></span></button>
                <button class="cbp-conv-no" v-hint="'ยกเลิก'" aria-label="ยกเลิก" @click.stop="pendingDeleteId = null"><span class="mdi mdi-close"></span></button>
              </span>
            </div>
            <p v-if="!conversations.length" class="cbp-empty">ยังไม่มีบทสนทนา</p>
          </div>
        </aside>

        <section v-if="expanded || !showList" class="cbp-chat">
          <div ref="scroll" class="cbp-msgs">
            <div v-if="!messages.length" class="cbp-hello">
              ถามได้เลย เช่น "สัปดาห์นี้จองกี่ใบ" หรือ "ยอดขายเดือนนี้เท่าไร"
            </div>
            <div v-for="(m, i) in messages" :key="i" class="cbp-row" :class="m.role">
              <div class="cbp-bubble" v-if="m.role === 'user'">{{ m.content }}</div>
              <div class="cbp-bubble cbp-bot" v-else v-html="formatMarkdown(m.content)"></div>
            </div>
            <div v-if="sending" class="cbp-row assistant">
              <div class="cbp-bubble cbp-bot cbp-typing">
                <span class="cb-dots" aria-hidden="true"><i></i><i></i><i></i></span>
                <span class="cb-typing-text">{{ thinkingText }}</span>
              </div>
            </div>
          </div>

          <div class="cbp-input">
            <textarea v-model="input" rows="1" :disabled="sending" placeholder="พิมพ์คำถาม…"
              @keydown.enter.exact.prevent="send"></textarea>
            <button class="cbp-send" :disabled="sending || !input.trim()" @click="send"><span class="mdi mdi-send"></span></button>
          </div>
        </section>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { sendChatMessage, listChatConversations, getChatConversation, deleteChatConversation } from './chatbotApi';
import { formatChatMarkdown, THINKING_STEPS } from '@/utils/chatMarkdown';

const FAB_SIZE = 56; // ต้องตรงกับ width/height ของ .cbfab ใน <style> ด้านล่าง
const FAB_MARGIN = 8; // ระยะห่างขอบจอขั้นต่ำตอนลากไปสุดขอบ
const FAB_POS_KEY = 'cbfab_pos_v1';

export default {
  name: 'ChatbotFab',
  emits: ['onErrorHandler'],
  data() {
    return {
      open: false, expanded: false, showList: false,
      input: '', sending: false, messages: [], conversationId: null,
      conversations: [], pendingDeleteId: null, deletingId: null,
      thinkingStep: 0, thinkingTimer: null,
      fabPos: null, // null = ตำแหน่งเริ่มต้นจาก CSS (right/bottom) · {x,y} = ตำแหน่งที่ลากไว้ (px จากมุมซ้ายบน)
      dragging: false, dragStart: null, dragMoved: false, dragMovedResetTimer: null,
      headDrag: null, panelDragged: false, // ลาก panel ที่แถบหัว — ลากแล้วเลิกยึดติดปุ่ม จนกว่าจะปิด/ลากปุ่มใหม่
      panelPos: null, // null = ให้ CSS คุม (มุมขวาล่างเดิม) · {left,top,right,bottom} = คำนวณให้ชิดปุ่มปัจจุบัน
    };
  },
  computed: {
    thinkingText() {
      return THINKING_STEPS[Math.min(this.thinkingStep, THINKING_STEPS.length - 1)].text;
    },
    // ยังไม่เคยลาก = ปล่อยให้ CSS คุม right/bottom:20px ตามเดิม · ลากแล้ว = คุมด้วย left/top แทน
    // (ต้องเซ็ต right/bottom เป็น auto คู่กันเสมอ ไม่งั้น left+right ที่ตั้งพร้อมกันจะไปงอความกว้างปุ่ม)
    fabStyle() {
      if (!this.fabPos) return {};
      return { left: `${this.fabPos.x}px`, top: `${this.fabPos.y}px`, right: 'auto', bottom: 'auto' };
    },
  },
  watch: {
    // เปิด panel / ลากปุ่มขณะเปิดอยู่ / กดขยาย-ย่อ (ขนาด panel เปลี่ยน) — ทุกครั้งต้องคำนวณตำแหน่งใหม่
    // ให้ panel ยังชิดปุ่มเสมอ ไม่ใช่ค้างที่มุมขวาล่างแบบเดิม
    open(v) { if (v) this.$nextTick(this.updatePanelPos); },
    fabPos(n, o) {
      if (!this.open) return;
      // resize สร้าง object ใหม่ค่าเดิมทุกครั้ง — เลิกยึดตำแหน่งที่ผู้ใช้ลากไว้เฉพาะเมื่อปุ่มขยับจริง
      if (!o || n.x !== o.x || n.y !== o.y) this.panelDragged = false;
      this.updatePanelPos();
    },
    expanded() { this.$nextTick(this.updatePanelPos); },
  },
  mounted() {
    const saved = this.loadFabPos();
    if (saved) this.fabPos = this.clampFabPos(saved);
    window.addEventListener('resize', this.onWindowResize);
  },
  unmounted() {
    if (this.thinkingTimer) clearTimeout(this.thinkingTimer); // ปิดหน้าต่างกลางคันแล้ว timer ต้องไม่ค้าง
    clearTimeout(this.dragMovedResetTimer);
    window.removeEventListener('resize', this.onWindowResize);
  },
  methods: {
    onWindowResize() {
      if (this.fabPos) this.fabPos = this.clampFabPos(this.fabPos);
      if (this.open) this.updatePanelPos();
    },
    // วาง panel ให้ชิดปุ่มเสมอ — ไม่ใช่ผูกกับมุมขวาล่างตายตัวแบบเดิม (เจ้าของทัก: ลาก FAB ไปแล้ว
    // panel ยังโผล่ที่เดิม รู้สึกไม่สัมพันธ์กัน) วัดขนาด panel จริงจาก DOM แทนเลข px คงที่ เพราะขนาด
    // ต่างกันตามสถานะ expanded/มือถือ (CSS เป็นคนกำหนด ไม่อยากคัดลอกเลขมาไว้ซ้ำใน JS)
    updatePanelPos() {
      // ผู้จัดการมือถือแบบขยายเต็มจอ (inset:10px) ต้องปล่อยให้ CSS คุมเอง ไม่งั้นค่า left/top
      // ที่เราคำนวณจะไปทับ layout เต็มจอที่ตั้งใจไว้
      if (this.expanded && window.innerWidth <= 700) { this.panelPos = null; return; }
      const btn = this.$refs.fab;
      const panel = this.$refs.panel;
      if (!btn || !panel) return;
      const r = btn.getBoundingClientRect();
      const pr = panel.getBoundingClientRect();
      const gap = 12; // ระยะห่างจากปุ่ม
      const margin = 8; // ระยะห่างขั้นต่ำจากขอบจอ
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (this.panelDragged) { // ผู้ใช้ลากมาวางเอง — แค่ดันกลับเข้าจอ (เช่นหลังขยาย) ไม่ย้ายกลับไปชิดปุ่ม
        this.setPanelXY(pr.left, pr.top, pr);
        return;
      }
      // แนวตั้ง: เปิดขึ้นด้านบนปุ่มถ้าที่ว่างด้านบนพอ (หรือมากกว่าด้านล่าง) ไม่งั้นเปิดลงด้านล่าง
      const spaceAbove = r.top;
      const spaceBelow = vh - r.bottom;
      let top = (spaceAbove >= pr.height + gap || spaceAbove > spaceBelow)
        ? r.top - gap - pr.height
        : r.bottom + gap;
      // แนวนอน: ชิดขอบเดียวกับปุ่ม (ปุ่มอยู่ซีกขวาจอ → panel ชิดขวาปุ่ม, ซีกซ้าย → ชิดซ้ายปุ่ม)
      let left = (r.left + r.width / 2 > vw / 2) ? r.right - pr.width : r.left;
      top = Math.min(Math.max(top, margin), Math.max(margin, vh - pr.height - margin));
      left = Math.min(Math.max(left, margin), Math.max(margin, vw - pr.width - margin));
      this.panelPos = { left: `${left}px`, top: `${top}px`, right: 'auto', bottom: 'auto' };
    },
    setPanelXY(left, top, pr) {
      const m = 8;
      const l = Math.min(Math.max(left, m), Math.max(m, window.innerWidth - pr.width - m));
      const t = Math.min(Math.max(top, m), Math.max(m, window.innerHeight - pr.height - m));
      this.panelPos = { left: `${l}px`, top: `${t}px`, right: 'auto', bottom: 'auto' };
    },
    onHeadDown(e) {
      if (e.button !== undefined && e.button !== 0) return;
      if (e.target.closest('button')) return; // กดปุ่มในแถบหัว = กดปุ่ม ไม่ใช่เริ่มลาก
      if (this.expanded && window.innerWidth <= 700) return; // เต็มจอมือถือ ไม่มีที่ให้ย้าย
      const pr = this.$refs.panel.getBoundingClientRect();
      this.headDrag = { x: e.clientX, y: e.clientY, left: pr.left, top: pr.top, pr };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    onHeadMove(e) {
      const d = this.headDrag;
      if (!d) return;
      this.panelDragged = true;
      this.setPanelXY(d.left + e.clientX - d.x, d.top + e.clientY - d.y, d.pr);
    },
    onHeadUp(e) {
      if (!this.headDrag) return;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      this.headDrag = null;
    },
    clampFabPos(pos) {
      const maxX = Math.max(FAB_MARGIN, window.innerWidth - FAB_SIZE - FAB_MARGIN);
      const maxY = Math.max(FAB_MARGIN, window.innerHeight - FAB_SIZE - FAB_MARGIN);
      return { x: Math.min(Math.max(pos.x, FAB_MARGIN), maxX), y: Math.min(Math.max(pos.y, FAB_MARGIN), maxY) };
    },
    loadFabPos() {
      try {
        const pos = JSON.parse(localStorage.getItem(FAB_POS_KEY) || 'null');
        // ค่าที่เพี้ยน (แก้ localStorage มือ/ของเก่าคนละรูปแบบ) ต้องไม่ทำให้ style พัง — ทิ้งแล้วใช้ค่าเริ่มต้น
        return pos && Number.isFinite(pos.x) && Number.isFinite(pos.y) ? pos : null;
      } catch (e) { return null; }
    },
    // ponytail: ลากด้วยเมาส์/นิ้วเดียวพอสำหรับ FAB ปุ่มเดียว ไม่รองรับ multi-touch/snap-to-edge
    onPointerDown(e) {
      if (e.button !== undefined && e.button !== 0) return; // ปุ่มหลักเท่านั้น (ไม่ยุ่งกับคลิกขวา)
      const rect = this.$refs.fab.getBoundingClientRect();
      this.dragStart = { x: e.clientX, y: e.clientY, left: rect.left, top: rect.top };
      this.dragMoved = false;
      this.$refs.fab.setPointerCapture(e.pointerId);
    },
    onPointerMove(e) {
      if (!this.dragStart) return;
      const dx = e.clientX - this.dragStart.x;
      const dy = e.clientY - this.dragStart.y;
      if (!this.dragMoved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) { this.dragMoved = true; this.dragging = true; }
      if (!this.dragMoved) return;
      this.fabPos = this.clampFabPos({ x: this.dragStart.left + dx, y: this.dragStart.top + dy });
    },
    onPointerUp(e) {
      if (!this.dragStart) return;
      this.$refs.fab.releasePointerCapture?.(e.pointerId);
      this.dragging = false;
      if (this.dragMoved) {
        try { localStorage.setItem(FAB_POS_KEY, JSON.stringify(this.fabPos)); } catch (err) { /* ที่เก็บเต็ม/ถูกปิด — ข้ามได้ ไม่ใช่ข้อมูลสำคัญ */ }
        // toggle() เป็นคนล้าง dragMoved ตามปกติ (ต้องรอให้ click ที่ตามหลัง pointerup มาถึงก่อน ถึงจะรู้
        // ว่าต้องกลืน click นั้นทิ้ง) แต่ pointercancel (นิ้วหลุด/ถูกขัดจังหวะกลางทาง) ไม่มี click ตามมา
        // เลยตาม spec — ถ้าไม่มีตัวสำรอง dragMoved จะค้าง true แล้วแตะครั้งถัดไปจะถูกกลืนไปเรื่อย ๆ
        clearTimeout(this.dragMovedResetTimer);
        this.dragMovedResetTimer = setTimeout(() => { this.dragMoved = false; }, 300);
      }
      this.dragStart = null;
    },
    startThinking() {
      this.thinkingStep = 0;
      const startedAt = Date.now();
      const tick = () => {
        const next = THINKING_STEPS[this.thinkingStep + 1];
        if (!next) return; // ขั้นสุดท้ายค้างไว้ ไม่วนกลับ (วนกลับ = ดูเหมือนเริ่มใหม่)
        // `at` เป็นเวลาสะสมจากตอนกดส่ง — ต้องลบเวลาที่ผ่านไปแล้ว ไม่งั้นขั้นที่ 3-4 จะเลื่อนไปไกลกว่าที่ตั้งใจ
        const delay = Math.max(next.at - (Date.now() - startedAt), 0);
        this.thinkingTimer = setTimeout(() => { this.thinkingStep += 1; tick(); }, delay);
      };
      tick();
    },
    stopThinking() {
      if (this.thinkingTimer) { clearTimeout(this.thinkingTimer); this.thinkingTimer = null; }
      this.thinkingStep = 0;
    },
    toggle() {
      // click เกิดหลัง pointerup เสมอ — ถ้าเพิ่งลาก (ขยับ > 4px) ต้องกลืน click นี้ทิ้ง ไม่งั้นปล่อยมือ
      // จากการลากจะเปิด/ปิด panel ไปด้วยโดยไม่ตั้งใจ
      if (this.dragMoved) { this.dragMoved = false; clearTimeout(this.dragMovedResetTimer); return; }
      this.open = !this.open;
      this.panelDragged = false;
      if (this.open) this.loadConversations();
      else { this.expanded = false; this.showList = false; this.pendingDeleteId = null; }
    },
    close() { this.open = false; this.panelDragged = false; this.expanded = false; this.showList = false; this.pendingDeleteId = null; },
    toggleList() {
      this.showList = !this.showList;
      if (this.showList) this.loadConversations();
    },
    newChat() {
      this.conversationId = null;
      this.messages = [];
      this.input = '';
      this.showList = false;
    },
    async loadConversations() {
      try {
        const data = await listChatConversations(this.baseURL, this.$store.getters.getToken);
        if (data && data.success) this.conversations = data.conversations || [];
      } catch (e) { /* เงียบ — รายการว่างไม่ใช่เรื่องร้ายแรง */ }
    },
    async openConversation(id) {
      this.pendingDeleteId = null;
      try {
        const token = this.$store.getters.getToken;
        const data = await getChatConversation(this.baseURL, token, id);
        if (data && data.success) {
          this.conversationId = id;
          this.messages = (data.messages || []).map((m) => ({ role: m.role, content: m.content }));
          this.showList = false;
          this.scrollDown();
        } else {
          this.$emit('onErrorHandler', (data && data.message) || 'เปิดบทสนทนาไม่สำเร็จ');
        }
      } catch (e) {
        this.$emit('onErrorHandler', 'เปิดบทสนทนาไม่สำเร็จ');
      }
    },
    async doDelete(id) {
      if (this.deletingId) return; // กันกดยืนยันซ้ำเร็วเกินกว่า Vue จะ re-render ซ่อนปุ่ม
      this.deletingId = id;
      this.pendingDeleteId = null;
      try {
        const token = this.$store.getters.getToken;
        await deleteChatConversation(this.baseURL, token, id);
        this.conversations = this.conversations.filter((c) => c.conversationid !== id);
        if (this.conversationId === id) this.newChat();
      } catch (e) {
        this.$emit('onErrorHandler', 'ลบบทสนทนาไม่สำเร็จ');
      } finally {
        this.deletingId = null;
      }
    },
    async send() {
      const text = this.input.trim();
      if (!text || this.sending) return;
      this.messages.push({ role: 'user', content: text });
      this.input = '';
      this.sending = true;
      this.startThinking();
      this.scrollDown();
      try {
        const token = this.$store.getters.getToken;
        const data = await sendChatMessage(this.baseURL, token, { conversationId: this.conversationId, text });
        if (data && data.success) {
          this.conversationId = data.conversationId;
          this.messages.push({ role: 'assistant', content: data.reply });
          this.loadConversations(); // อัปเดตหัวข้อ/ลำดับในรายการ
        } else {
          this.messages.push({ role: 'assistant', content: (data && data.message) || 'ขออภัย ระบบขัดข้อง' });
        }
      } catch (e) {
        this.messages.push({ role: 'assistant', content: 'ขออภัย ระบบขัดข้องหรือใช้เวลานานเกินไป กรุณาลองใหม่' });
      } finally {
        this.stopThinking();
        this.sending = false;
        this.scrollDown();
      }
    },
    scrollDown() {
      this.$nextTick(() => { const el = this.$refs.scroll; if (el) el.scrollTop = el.scrollHeight; });
    },
    formatMarkdown(text) { return formatChatMarkdown(text); },
  },
};
</script>

<style scoped>
.cbfab { position: fixed; right: 20px; bottom: 20px; z-index: 2600; width: 56px; height: 56px;
  border-radius: 50%; border: none; cursor: grab; color: var(--c-on-primary, #fff); font-size: 1.6rem;
  background: var(--c-primary); box-shadow: var(--shadow-pop, 0 6px 18px rgba(0,0,0,0.25));
  touch-action: none; } /* touch-action:none กันเบราว์เซอร์ตีความลากเป็นการเลื่อนหน้าจอ */
.cbfab:hover { filter: brightness(1.05); }
.cbfab.open { background: var(--c-text-muted); }
.cbfab.dragging { cursor: grabbing; transition: none; } /* ระหว่างลากต้องตามนิ้ว/เมาส์ทันที ไม่มีดีเลย์ */
</style>

<style>
/* panel อยู่ที่ body (Teleport) จึงไม่ scoped — ตั้งชื่อคลาสให้เฉพาะเจาะจงกันชน */
.cbfab-panel { position: fixed; right: 20px; bottom: 88px; z-index: 2600;
  width: min(380px, calc(100vw - 32px)); height: min(560px, calc(100vh - 130px));
  display: flex; flex-direction: column; overflow: hidden;
  background: var(--c-surface); color: var(--c-text-body);
  border: 1px solid var(--c-border); border-radius: var(--radius-lg, 16px); box-shadow: var(--shadow-lg, 0 12px 40px rgba(0,0,0,0.28)); }
/* ไม่มี transition ขนาด: updatePanelPos วัดขนาดจริงหลังขยาย ถ้ามีอนิเมชันจะวัดได้ขนาดเก่า แล้วล้นจอ */
/* ขยาย = แค่โตขึ้นเป็น overlay ใหญ่ ยังลอยอยู่หน้าเดิม ไม่สลับ state */
.cbfab-panel.expanded { width: min(860px, calc(100vw - 40px)); height: min(760px, calc(100vh - 64px)); }
@media (max-width: 700px) {
  .cbfab-panel.expanded { left: 10px; right: 10px; top: 10px; bottom: 10px; width: auto; height: auto; }
}
.cbp-head { display: flex; align-items: center; gap: 4px; padding: 10px 12px;
  background: var(--c-primary); color: var(--c-on-primary, #fff); cursor: grab; touch-action: none; user-select: none; }
.cbp-title { font-weight: 600; display: inline-flex; align-items: center; gap: 6px; flex: 1; min-width: 0; }
.cbp-actions { display: inline-flex; gap: 2px; }
.cbp-ic { background: transparent; border: none; color: var(--c-on-primary, #fff); cursor: pointer; font-size: 1.1rem; padding: 4px 6px; border-radius: 8px; flex-shrink: 0; }
.cbp-ic:hover { background: rgba(255,255,255,0.18); }

.cbp-body { flex: 1; display: flex; min-height: 0; overflow: hidden; }
.cbp-body.split .cbp-list { width: 220px; flex-shrink: 0; border-right: 1px solid var(--c-border); }
.cbp-body.split .cbp-chat { flex: 1; min-width: 0; }
.cbp-body:not(.split) .cbp-list, .cbp-body:not(.split) .cbp-chat { width: 100%; }

.cbp-list { display: flex; flex-direction: column; min-height: 0; padding: 8px; gap: 8px; overflow: hidden; }
.cbp-new { display: flex; align-items: center; justify-content: center; gap: 6px; padding: 8px;
  border-radius: 10px; border: 1px solid var(--c-border); background: var(--c-surface); color: var(--c-text-body);
  cursor: pointer; font-weight: 600; font-size: 0.85rem; flex-shrink: 0; }
.cbp-new:hover { background: var(--c-surface-2); }
.cbp-convs { overflow-y: auto; display: flex; flex-direction: column; gap: 4px; min-height: 0; }
.cbp-conv-row { display: flex; align-items: center; gap: 2px; border-radius: 8px; }
.cbp-conv-row.on { background: var(--c-surface-2); }
.cbp-conv { flex: 1; min-width: 0; text-align: left; padding: 7px 8px; border-radius: 8px; border: none;
  background: transparent; color: var(--c-text-body); cursor: pointer; font-size: 0.85rem; }
.cbp-conv:hover { background: var(--c-surface-2); }
.cbp-conv-title { display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cbp-conv-del { background: transparent; border: none; color: var(--c-text-muted); cursor: pointer; padding: 4px 6px; border-radius: 6px; flex-shrink: 0; }
.cbp-conv-del:hover { background: var(--c-error-bg, rgba(220,38,38,0.12)); color: var(--c-error, #dc2626); }
.cbp-conv-confirm { display: inline-flex; gap: 2px; flex-shrink: 0; padding-right: 4px; }
.cbp-conv-yes, .cbp-conv-no { background: transparent; border: none; cursor: pointer; padding: 4px 6px; border-radius: 6px; }
.cbp-conv-yes { color: var(--c-error, #dc2626); }
.cbp-conv-yes:hover { background: var(--c-error-bg, rgba(220,38,38,0.12)); }
.cbp-conv-no:hover { background: var(--c-surface-2); }
.cbp-empty { color: var(--c-text-muted); font-size: 0.82rem; padding: 8px; }

.cbp-chat { display: flex; flex-direction: column; min-height: 0; min-width: 0; }
.cbp-msgs { flex: 1; overflow-y: auto; padding: 12px; display: flex; flex-direction: column; gap: 8px; }
.cbp-hello { color: var(--c-text-muted); font-size: 0.88rem; }
.cbp-row { display: flex; }
.cbp-row.user { justify-content: flex-end; }
.cbp-row.assistant { justify-content: flex-start; }
.cbp-bubble { max-width: 85%; padding: 8px 12px; border-radius: 12px; line-height: 1.5; word-break: break-word; }
.cbp-row.user .cbp-bubble { background: var(--c-primary); color: var(--c-on-primary, #fff); border-bottom-right-radius: 4px; white-space: pre-wrap; }
.cbp-bot { background: var(--c-surface-2); color: var(--c-text-body); border-bottom-left-radius: 4px; }
.cbp-bot p { margin: 0 0 6px; }
.cbp-bot ul { margin: 4px 0; padding-left: 18px; }
.cbp-bot h4, .cbp-bot h5 { margin: 8px 0 4px; }
/* .cbp-typing วางตัวเหมือน .cb-typing ของหน้าเต็ม — จุดเด้ง/ตาราง อยู่ใน istar-pages.css ที่เดียว */
.cbp-typing { opacity: 0.85; display: flex; align-items: center; gap: 8px; }
.cbp-input { display: flex; gap: 8px; padding: 10px; border-top: 1px solid var(--c-border); }
.cbp-input textarea { flex: 1; resize: none; border: 1px solid var(--c-border); border-radius: 10px;
  padding: 8px 10px; font: inherit; max-height: 100px; background: var(--c-surface); color: inherit; }
.cbp-send { width: 40px; border: none; border-radius: 10px; background: var(--c-primary); color: var(--c-on-primary, #fff); cursor: pointer; }
.cbp-send:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
